import { Button, Chip, Typography } from '@mui/material'
import styled from 'styled-components'
import { fontFamily, fontWeight } from 'theme'

import { OrderDisplayStatus } from './OrderManagement'

const getStatusStyles = (status: OrderDisplayStatus) => {
  switch (status) {
    case OrderDisplayStatus.COMPLETED:
      return {
        backgroundColor: 'rgba(50, 150, 93, 0.1)',
        color: '#32965D',
      }
    case OrderDisplayStatus.CANCELLED:
      return {
        backgroundColor: 'rgba(193, 48, 48, 0.1)',
        color: '#C13030',
      }
    default:
      return {
        backgroundColor: 'transparent',
        color: '#000',
      }
  }
}

export const StyledChip = styled(Chip)<{ status: OrderDisplayStatus }>(({ status }) => ({
  '&&': {
    fontFamily: fontFamily.inter,
    fontSize: '13px',
    fontWeight: fontWeight.medium,
    borderRadius: '5px',
    padding: 0,
    height: '23px',
    backgroundColor: getStatusStyles(status).backgroundColor,
    color: getStatusStyles(status).color,
    textTransform: 'capitalize',
  },
  '& .MuiChip-icon': {
    color: `${getStatusStyles(status).color} !important`,
    marginLeft: 0,
    marginRight: 0,
  },
}))

export const DrawerHeader = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '15px',
  borderBottom: 'solid 1px #ccc',

  '& h2': {
    fontFamily: fontFamily.inter,
    fontSize: '20px',
    fontWeight: fontWeight.semibold,
    margin: 0,
  },
})

export const DrawerContent = styled.div({
  display: 'flex',
  flexDirection: 'column',
  padding: '0 15px',
  height: 'calc(100vh - 60px)', // Subtract header height
  overflowY: 'auto' as const,
  position: 'relative',
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f1f1',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#888',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: '#555',
  },
})

export const Row = styled.div(() => ({
  display: 'flex',
  padding: '2px 0',
  flexDirection: 'row' as const,
  fontFamily: fontFamily.inter,
  fontSize: 13,
  color: '#292929',
  width: '100%',
  fontWeight: fontWeight.medium,
}))

export const HeaderRow = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '16px',
  width: '100%',
  paddingTop: '15px',
  paddingRight: '0px',
  '& > div:first-of-type': {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    flex: 1
  }
})

export const RowSingle = styled.div({
  display: 'flex',
  flexDirection: 'column',
  padding: '8px 0',
})

export const RowPair = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
  gap: '16px',
})

export const Label = styled.div({
  fontFamily: 'Inter, sans-serif',
  fontSize: '13px',
  marginBottom: 10,
  fontWeight: fontWeight.semibold,
  width: 'auto',
  whiteSpace: 'nowrap',
})

export const Value = styled.div<{ horizontal?: boolean }>(({ horizontal }) => ({
  textAlign: 'left',
  color: '#292929',
  display: 'flex',
  flexDirection: horizontal ? 'row' : 'column',
  alignItems: horizontal ? 'center' : 'flex-start',
  justifyContent: 'flex-start',
  gap: horizontal ? '8px' : '0',
  marginBottom: 10,
  paddingLeft: '5px',
}))

export const WrappedValue = styled(Value)({
  wordBreak: 'break-all',
});

export const PlatformTicket = styled.div({
  textAlign: 'left',
  color: '#292929',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  justifyContent: 'flex-start',
  gap: '0',
});

export const CustomLink = styled.a({
  paddingLeft: '5px',
  color: '#1B51A4',
  textDecoration: 'none',
  '&:hover': {
    textDecoration: 'none',
    cursor: 'pointer',
  },
})

export const InfoItem = styled.div({
  marginBottom: '10px',
  textAlign: 'left',
  paddingLeft: '5px',
  color: '#292929',
  '& a': {
    color: '#1B51A4',
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  '&:last-child': {
    marginBottom: '0',
  },
})

export const SubmissionTime = styled(Typography)({
  '&&': {
    fontFamily: fontFamily.inter,
    fontSize: '13px',
  },
})

export const SubmissionDate = styled(Typography)({
  '&&': {
    fontFamily: fontFamily.inter,
    fontSize: '13px',
  },
})

export const ButtonContainer = styled.div(() => ({
  '&&': {
    display: 'flex',
    marginTop: 30,
  },
}))

export const StyledButton = styled(Button)({
  '&&': {
    backgroundColor: '#fff',
    border: '1px solid #1B51A4',
    borderRadius: '6px',
    color: '#1B51A4',
    boxShadow: 'none',
    fontFamily: fontFamily.inter,
    fontSize: 15,
    fontWeight: fontWeight.semibold,
    marginRight: 30,
    textTransform: 'capitalize',
    '&:hover': {
      boxShadow: 'none',
    },
  },
})

export const ReadyForPickupSection = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  minWidth: '140px',
})

export const MarkedReadyForPickup = styled.div({
  color: '#777777',
  fontFamily: fontFamily.inter,
  fontSize: '12px',
  fontWeight: fontWeight.medium,
  whiteSpace: 'nowrap',
  marginTop: '4px',
  textAlign: 'center',
})

export const SectionHeader = styled.div({
  fontFamily: fontFamily.inter,
  fontWeight: fontWeight.semibold,
  fontSize: '20px',
  color: '#222',
  margin: '0px 0 16px 0',
  letterSpacing: '0.5px',
});

export const Divider = styled.div({
  width: '100%',
  height: '1px',
  background: 'rgba(27, 81, 164, 0.16)',
  margin: '16px 0',
  position: 'relative',
  zIndex: 1,
  flexShrink: 0,
  display: 'block',
})

export const FixedButtonBar = styled.div({
  position: 'sticky',
  bottom: 0,
  left: 0,
  right: 0,
  background: '#fff',
  borderTop: '1px solid #E0E0E0',
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '12px',
  padding: '16px',
  zIndex: 1201,
  boxSizing: 'border-box',
});

export const DrawerContentContainer = styled.div<{ hasIframe?: boolean; isDesktop?: boolean }>(({ hasIframe, isDesktop }) => ({
  display: hasIframe && isDesktop ? 'flex' : undefined,
  flexDirection: hasIframe && isDesktop ? 'row' : undefined,
  height: '100%',
  overflowX: 'hidden'
}));

export const IframeContainer = styled.div({
  width: 792,
  maxWidth: 792,
  background: '#fff',
  display: 'flex',
  flexDirection: 'column',
  paddingLeft: 16
});

export const IframeWrapper = styled.div({
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

export const OrderDetailsContainer = styled.div({
  flex: 1,
  background: '#fff',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  overflowX: 'hidden',
  position: 'relative',
  paddingLeft: 16,
  paddingRight: 16
});

export const OrderDetailsContent = styled.div({
  maxWidth: 420,
  wordWrap: 'break-word'
});

export const MobileIframeContainer = styled.div({
  width: '100%'
});

export const ScrollableContent = styled.div({
  flex: 1,
  overflowY: 'scroll',
  minHeight: 0
});
